import { fmtIntCA } from "@/lib/format";

import type { PerLineYear } from "../_lib/table";

type Props = {
  label: string;
  series: PerLineYear[];
  ariaLabel: string;
};

const W = 680;
const H = 200;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 16;
const PAD_B = 40;

function niceCeiling(v: number): number {
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / mag) * mag;
}

/**
 * Per-line multi-year budget-vs-actual view (spec FR-010/FR-011/FR-012).
 * Side-by-side bars per year (budget then actual) — NOT a flat-actual
 * line — so the variance is legible at a glance. Discontinuous coverage
 * (the 2022 expense-taxonomy break) is shown by ABSENCE of the missing
 * years, never back-filled. A year with no budget (the in-kind line)
 * renders the actual bar with an explicit "not budgeted" tick rather
 * than a fabricated zero-budget bar.
 *
 * Focused variant rather than reusing /finances/revenues' VarianceChart:
 * that component lives in another sub-page's _components and rejects any
 * null-budget row outright; budget-vs-actual must instead surface the
 * "not budgeted" case honestly per the spec Edge Cases.
 */
export function PerLineVarianceView({ label, series, ariaLabel }: Props) {
  if (series.length === 0) return null;

  const values = series.flatMap((r) => [
    r.budget_thousands ?? 0,
    r.actual_thousands ?? 0,
  ]);
  const yMax = niceCeiling(Math.max(...values, 1));
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const groupW = innerW / series.length;
  const barW = (groupW * 0.7) / 2;

  const y = (v: number) => PAD_T + innerH - (v / yMax) * innerH;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-4 text-[12px] cs-mono text-ink-3">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 bg-ink-3/40" /> budget
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 bg-signal" /> actual
        </span>
        <span>{series.length} observed year(s)</span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={ariaLabel}
        className="w-full max-w-3xl"
      >
        <line
          x1={PAD_L}
          y1={PAD_T + innerH}
          x2={W - PAD_R}
          y2={PAD_T + innerH}
          className="stroke-rule"
          strokeWidth={1}
        />
        {series.map((r, i) => {
          const gx = PAD_L + i * groupW + groupW * 0.15;
          const aH = ((r.actual_thousands ?? 0) / yMax) * innerH;
          const bH = ((r.budget_thousands ?? 0) / yMax) * innerH;
          return (
            <g key={r.year}>
              {r.budget_thousands === null ? (
                <text
                  x={gx + barW}
                  y={PAD_T + innerH - 4}
                  textAnchor="middle"
                  className="fill-ink-3 cs-mono"
                  fontSize={9}
                >
                  not budgeted
                </text>
              ) : (
                <rect
                  x={gx}
                  y={y(r.budget_thousands)}
                  width={barW}
                  height={Math.max(bH, 0)}
                  className="fill-ink-3/40"
                />
              )}
              <rect
                x={gx + barW}
                y={y(r.actual_thousands ?? 0)}
                width={barW}
                height={Math.max(aH, 0)}
                className="fill-signal"
              />
              <text
                x={gx + barW}
                y={H - PAD_B + 16}
                textAnchor="middle"
                className="fill-ink-3 cs-mono"
                fontSize={11}
              >
                {r.year}
              </text>
            </g>
          );
        })}
      </svg>
      <table className="w-full text-xs tabular-nums">
        <caption className="sr-only">
          {label} — budget vs actual by year, thousands of CAD
        </caption>
        <thead>
          <tr className="text-left cs-mono text-[11px] uppercase tracking-wider text-ink-3">
            <th scope="col" className="pb-1">
              Year
            </th>
            <th scope="col" className="pb-1 text-right">
              Budget
            </th>
            <th scope="col" className="pb-1 text-right">
              Actual
            </th>
            <th scope="col" className="pb-1 text-right">
              Source
            </th>
          </tr>
        </thead>
        <tbody>
          {series.map((r) => (
            <tr key={r.year} className="border-t border-rule">
              <td className="py-1">{r.year}</td>
              <td className="py-1 text-right">
                {r.budget_thousands === null
                  ? "not budgeted"
                  : `$${fmtIntCA(Math.round(r.budget_thousands))}`}
              </td>
              <td className="py-1 text-right">
                {r.actual_thousands === null
                  ? "—"
                  : `$${fmtIntCA(Math.round(r.actual_thousands))}`}
              </td>
              <td className="py-1 text-right">
                {r.source_pdf_url && r.actual_source_page ? (
                  <a
                    href={`${r.source_pdf_url}#page=${r.actual_source_page}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cs-mono text-[11px] text-ink-3 cs-rule-underline hover:text-signal transition-colors"
                  >
                    AFR p.{r.actual_source_page}
                  </a>
                ) : (
                  <span className="cs-mono text-[11px] text-ink-3">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
